import type { TopProspects } from "../../common/types.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import type { Col, DataTableRow } from "../components/DataTable/index.tsx";
import { RatingsStatsPopover } from "../components/RatingsStatsPopover/index.tsx";
import { useLocal } from "../util/local.ts";
import { helpers } from "../util/helpers.ts";

const cols: Col[] = [
	{
		title: "#",
		desc: "Annual prospect ranking",
		sortType: "number",
		sortSequence: ["asc", "desc"],
	},
	{ title: "Name", sortType: "name", sortSequence: ["asc", "desc"] },
	{
		title: "Org",
		desc: "Organization at the time of the ranking",
		sortType: "string",
		sortSequence: ["asc", "desc"],
	},
	{ title: "Pos", sortType: "pos", sortSequence: ["asc", "desc"] },
	{ title: "Age", sortType: "number", sortSequence: ["asc", "desc"] },
	{ title: "Ovr", sortType: "number", sortSequence: ["desc", "asc"] },
	{ title: "Pot", sortType: "number", sortSequence: ["desc", "asc"] },
];

const farmCols: Col[] = [
	{ title: "#", desc: "Farm system rank", sortType: "number", sortSequence: ["asc", "desc"] },
	{ title: "Org", sortType: "string", sortSequence: ["asc", "desc"] },
	{
		title: "Points",
		desc: "100 for the #1 prospect down to 1 for #100, added up for each organization",
		sortType: "number",
		sortSequence: ["desc", "asc"],
	},
	{ title: "Top 100", desc: "Prospects in the Top 100", sortType: "number", sortSequence: ["desc", "asc"] },
];

type FarmRow = { tid: number; abbrev: string; points: number; prospects: number };

const Top100Prospects = ({
	ranking,
	farms,
	userTid,
}: {
	ranking: TopProspects;
	farms: FarmRow[];
	userTid: number;
}) => {
	const { challengeNoRatings } = useLocal(["challengeNoRatings"]);
	const rows: DataTableRow[] = ranking.players.map((p, i) => ({
		key: p.pid,
		classNames: { "table-info": p.tid === userTid },
		data: [
			i + 1,
			{
				value: (
					<>
						<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>{" "}
						<RatingsStatsPopover
							pid={p.pid}
							season={ranking.season}
							minorLeague
						/>
					</>
				),
				sortValue: p.name,
				searchValue: p.name,
			},
			{
				value: (
					<a href={helpers.leagueUrl(["minor_league", `${p.abbrev}_${p.tid}`])}>
						{p.abbrev}
					</a>
				),
				sortValue: p.abbrev,
				searchValue: p.abbrev,
			},
			p.pos,
			p.age,
			challengeNoRatings ? null : p.ovr,
			challengeNoRatings ? null : p.pot,
		],
	}));
	const farmRows: DataTableRow[] = farms.map((row, i) => ({
		key: row.tid,
		classNames: { "table-info": row.tid === userTid },
		data: [
			i + 1,
			{
				value: (
					<a href={helpers.leagueUrl(["minor_league", `${row.abbrev}_${row.tid}`])}>
						{row.abbrev}
					</a>
				),
				sortValue: row.abbrev,
				searchValue: row.abbrev,
			},
			row.points,
			row.prospects,
		],
	}));
	return (
		<>
			<h2>Farm System Ranking — {ranking.season}</h2>
			<p>
				Organizations ranked by their prospects in the Top 100 below. This
				ranking is informational and does not affect fan hype. The annual hype
				adjustment comes from the final Minor League standings.
			</p>
			{farmRows.length > 0 ? (
				<DataTable
					className="mb-4"
					cols={farmCols}
					rows={farmRows}
					defaultSort={[0, "asc"]}
					name="MinorLeagueFarmRanking"
				/>
			) : null}
			<h2>Top 100 Prospects — {ranking.season}</h2>
			<p>
				League-wide ranking of signed minor leaguers who are still prospects,
				like MLB Pipeline: 25 or younger and rookie-eligible (no more than 130
				at bats or 50 innings in the majors). Ordered by scouted POT, then OVR,
				then younger age. Your organization is highlighted.
			</p>
			<p className="text-body-secondary">
				The list refreshes at the start of each regular season, after minor
				league rosters are prepared. It stays fixed during the season, including
				the organization and ratings recorded at the time. Existing leagues get
				an initial list on their first visit.
			</p>
			{rows.length > 0 ? (
				<DataTable
					cols={cols}
					rows={rows}
					defaultSort={[0, "asc"]}
					name="MinorLeagueTop100"
					pagination
				/>
			) : (
				<p className="text-body-secondary">
					No eligible prospects yet. The list refreshes when the regular season
					begins.
				</p>
			)}
		</>
	);
};

export default Top100Prospects;
